import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ReservationStatusBadge } from '@/modules/reservations/components';
import { useReservations } from '@/modules/reservations/hooks';
import {
  RESERVATION_STATUS_LABELS,
  type ReservationStatus,
} from '@/modules/reservations/types';
import { filterReservations } from '@/modules/reservations/utils/reservationFilters';
import { modulePaths } from '@/routes/paths';
import { useAuth } from '@/shared/hooks/useAuth';

const STATUS_FILTER_OPTIONS: Array<ReservationStatus | 'all'> = [
  'all',
  'draft',
  'confirmed',
  'in_progress',
  'completed',
  'cancelled',
];

export function ReservationsListPage() {
  const { reservations, loading, error } = useReservations();
  const { hasPermission } = useAuth();
  const canWrite = hasPermission('reservations.write');

  const [statusFilter, setStatusFilter] = useState<ReservationStatus | 'all'>(
    'all',
  );
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const filtered = useMemo(
    () =>
      filterReservations(reservations, {
        status: statusFilter,
        search,
        ...(dateFrom ? { dateFrom } : {}),
        ...(dateTo ? { dateTo } : {}),
      }),
    [reservations, statusFilter, search, dateFrom, dateTo],
  );

  return (
    <section>
      <div className="inventory-page-header">
        <h1>Reservas</h1>
        {canWrite ? (
          <Link
            to={`${modulePaths.reservations}/nueva`}
            className="auth-button inventory-page-header__btn"
          >
            Nueva reserva
          </Link>
        ) : null}
      </div>

      <div className="reservation-filters users-form-grid">
        <label className="auth-label">
          Estado
          <select
            value={statusFilter}
            onChange={(e) =>
              setStatusFilter(e.target.value as ReservationStatus | 'all')
            }
          >
            {STATUS_FILTER_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s === 'all' ? 'Todos' : RESERVATION_STATUS_LABELS[s]}
              </option>
            ))}
          </select>
        </label>
        <label className="auth-label">
          Desde
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
          />
        </label>
        <label className="auth-label">
          Hasta
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
          />
        </label>
        <label className="auth-label">
          Buscar
          <input
            type="search"
            placeholder="Título o cliente"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>
      </div>

      {loading ? <p className="auth-muted">Cargando reservas…</p> : null}
      {error ? <p className="auth-error">{error}</p> : null}

      {!loading && filtered.length === 0 ? (
        <p className="auth-muted">No hay reservas con los filtros aplicados.</p>
      ) : null}

      {!loading && filtered.length > 0 ? (
        <table className="users-table">
          <thead>
            <tr>
              <th>Título</th>
              <th>Cliente</th>
              <th>Desde</th>
              <th>Hasta</th>
              <th>Estado</th>
              <th>Ítems</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.id}>
                <td>{r.title}</td>
                <td>{r.clientName || '—'}</td>
                <td>{r.startDate}</td>
                <td>{r.endDate}</td>
                <td>
                  <ReservationStatusBadge status={r.status} />
                </td>
                <td>{r.inventoryItemIds.length}</td>
                <td>
                  <Link
                    to={`${modulePaths.reservations}/${r.id}`}
                    className="link-button"
                  >
                    Ver
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </section>
  );
}
